import { API_URL } from '@/config/constants'
import { useState, useEffect } from 'react'

// Simple constant definition right in this file
const DEFAULT_AVATAR = 'https://api.dicebear.com/7.x/avataaars/svg'

export interface CdnImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string | null | undefined
  alt?: string
  width?: number | string
  height?: number | string
  className?: string
  fallbackSrc?: string
  showDefaultAvatar?: boolean // If false, don't show default avatar when src is empty
  onLoad?: () => void
  onError?: () => void
}

/**
 * Renders an image from the CDN with error handling and fallback
 */
export default function CdnImage({
  src,
  alt,
  width,
  height,
  className,
  fallbackSrc,
  showDefaultAvatar = true, // Default to true for backward compatibility
  onLoad,
  onError,
  ...props
}: CdnImageProps) {
  const [error, setError] = useState(false)
  const [imageUrl, setImageUrl] = useState<string>('')

  useEffect(() => {
    if (!src) {
      // Only show default avatar if showDefaultAvatar is true
      if (showDefaultAvatar) {
        setImageUrl(fallbackSrc || DEFAULT_AVATAR)
      } else {
        setImageUrl('') // Empty string means don't render
      }
      return
    }

    // Check if this is a CDN URL that needs proxying
    if (
      src.includes(import.meta.env.VITE_CDN_BASE_URL) ||
      src.includes('46.101.199.181/cdn')
    ) {
      // If it's not already proxied, proxy it
      if (!src.includes('/server/health/proxy/image')) {
    // console.logProxying CDN URL for preview:', src)
        setImageUrl(
          `${API_URL}/health/proxy/image?url=${encodeURIComponent(src)}`
        )
        return
      }
    }

    // For all other URLs, use the source directly
    setImageUrl(src)
  }, [src, fallbackSrc])

  /**
   * Handle image load error by showing fallback
   */
  const handleError = () => {
    // console.error('Failed to load image:', imageUrl)
    setError(true)

    // Call the user provided error handler
    if (onError) {
      onError()
    }
  }

  /**
   * Handle successful image load
   */
  const handleLoad = () => {
    // Call the user provided load handler
    if (onLoad) {
      onLoad()
    }
  }

  // Don't render anything if showDefaultAvatar is false and there's no valid image
  if (!showDefaultAvatar && (!imageUrl || imageUrl === '' || imageUrl === DEFAULT_AVATAR)) {
    return null
  }

  // Show fallback image if there is an error
  if (error) {
    // Only show fallback if showDefaultAvatar is true
    if (showDefaultAvatar) {
      return (
        <img
          src={fallbackSrc || DEFAULT_AVATAR}
          alt={alt || 'Default Avatar'}
          width={width}
          height={height}
          className={className}
          {...props}
        />
      )
    }
    return null
  }

  // Don't render if imageUrl is empty and showDefaultAvatar is false
  if (!imageUrl || imageUrl === '') {
    return null
  }

  // Show the actual image
  return (
    <img
      src={imageUrl}
      alt={alt || 'Image'}
      width={width}
      height={height}
      className={className}
      onError={handleError}
      onLoad={handleLoad}
      {...props}
    />
  )
}
